import type { GridColDef } from '@mui/x-data-grid';
import type { PaymentOverview } from '@vy/api-definition';
import type { ValueOf } from '@vy/utils';
import { isNotNullOrUndefined } from '@vy/utils';
import { vyDate } from '@vy/utils';
import { getSelectorAttribute, paymentsPageSelectors } from '@vy/app-selectors';
import { Price } from '../../../components/design/Price';
import { getEmptyCellContent } from '../../../services/table/get-empty-cell-content';
import { compareOptionalDatesDescending, formatDate } from '../../../utils/date';
import { PaymentOverviewTableDotMenu } from './payments-page-components/PaymentOverviewTableDotMenu';
import { getPaymentProviderNameToDisplay } from '../../invoices/InvoicesPage/invoices-page-components/MarkAsPaidDialog/constants/paymentProviderMap';

export const PAYMENTS_COLUMNS_KEYS = Object.freeze({
  CUSTOMER_NAME: 'customerName',
  AMOUNT: 'amount',
  INVOICE_NUMBER: 'invoiceNumber',
  CURRENCY: 'currency',
  PAYMENT_PROVIDER: 'paymentMethod',
  STATUS: 'status',
  CREATION_TIME: 'externalPayment.chargeTime',
  DOT_MENU: 'actionMenu',
});

export type PaymentsColumnsKey = ValueOf<typeof PAYMENTS_COLUMNS_KEYS>;

export const PAYMENT_OVERVIEW_TABLE_COLUMNS: GridColDef<PaymentOverview>[] = [
  {
    field: PAYMENTS_COLUMNS_KEYS.CUSTOMER_NAME,
    headerName: 'Customer',
    headerClassName: 'pl-6',
    cellClassName: 'pl-6',
    flex: 1,
    renderCell: ({ row }) => (
      <span
        className="font-sans text-black overflow-hidden text-ellipsis whitespace-nowrap block"
        {...getSelectorAttribute(paymentsPageSelectors.columns.customerName)}
      >
        {row.customerName}
      </span>
    ),
  },
  {
    field: PAYMENTS_COLUMNS_KEYS.AMOUNT,
    headerName: 'Amount',
    flex: 1,
    renderCell: (params) => {
      const amount = params.row.amount;
      const currency = params.row.currency;

      return (
        <span
          className="text-label font-sans"
          {...getSelectorAttribute(paymentsPageSelectors.columns.amount)}
        >
          <Price className="font-sans text-black" sx={{ fontSize: 14 }} currency={currency}>
            {amount}
          </Price>
        </span>
      );
    },
  },
  {
    field: PAYMENTS_COLUMNS_KEYS.INVOICE_NUMBER,
    headerName: 'INV Number',
    flex: 1,
    renderCell: ({ row }) => {
      const invoiceNumber = row.invoiceNumber;

      return (
        <span
          className="font-sans text-black"
          {...getSelectorAttribute(paymentsPageSelectors.columns.invoiceNumber)}
        >
          {isNotNullOrUndefined(invoiceNumber) ? invoiceNumber : getEmptyCellContent()}
        </span>
      );
    },
  },
  {
    field: PAYMENTS_COLUMNS_KEYS.CURRENCY,
    headerName: 'Currency',
    flex: 1,
    renderCell: ({ row }) => (
      <span
        className="font-sans text-black"
        {...getSelectorAttribute(paymentsPageSelectors.columns.currency)}
      >
        {row.currency}
      </span>
    ),
  },
  {
    field: PAYMENTS_COLUMNS_KEYS.STATUS,
    headerName: 'Status',
    flex: 1,
    renderCell: ({ row }) => (
      <span
        className="font-sans text-black"
        {...getSelectorAttribute(paymentsPageSelectors.columns.status)}
      >
        {row.status}
      </span>
    ),
  },
  {
    field: PAYMENTS_COLUMNS_KEYS.CREATION_TIME,
    valueGetter: (_value, row) => row.externalPayment?.chargeTime,
    headerName: 'Transaction date',
    flex: 1,
    renderCell: ({ value }) => (
      <span
        className="font-sans text-black"
        {...getSelectorAttribute(paymentsPageSelectors.columns.transactionDate)}
      >
        {isNotNullOrUndefined(value) ? formatDate(vyDate(value)) : getEmptyCellContent()}
      </span>
    ),
    sortComparator: (firstDate, secondDate) => compareOptionalDatesDescending(firstDate, secondDate),
  },
  {
    field: PAYMENTS_COLUMNS_KEYS.PAYMENT_PROVIDER,
    headerName: 'Provider',
    flex: 1,
    renderCell: ({ row }) => {
      const paymentMethod = isNotNullOrUndefined(row.paymentMethod) ? row.paymentMethod : '---';
      const displayProviderName = getPaymentProviderNameToDisplay(paymentMethod);

      return (
        <span
          className="font-sans text-black"
          {...getSelectorAttribute(paymentsPageSelectors.columns.provider)}
        >
          {displayProviderName}
        </span>
      );
    },
  },
  {
    field: PAYMENTS_COLUMNS_KEYS.DOT_MENU,
    valueGetter: (_value, row) => row.invoiceId,
    headerName: '',
    width: 30,
    filterable: false,
    sortable: false,
    headerClassName: 'bg-white',
    cellClassName: 'bg-white',
    renderCell: ({ row }) => (
      <PaymentOverviewTableDotMenu
        invoiceId={row.invoiceId}
        externalInvoiceUrl={row.externalPayment?.receiptUrl}
        paymentId={row.id}
        customerId={row.customerId}
      />
    ),
  },
];

export const PAYMENTS_COLUMNS = PAYMENT_OVERVIEW_TABLE_COLUMNS;
